/**
 * EXPLODE × the three legacy primitives a block definition may hold — the heavy
 * `POLYLINE`, the `ACAD_TABLE` and the `OLE2FRAME` (`drawing-model/
 * entityPolylineTableOle.ts`).
 *
 * # The defect this closes
 *
 * `blockChildEntitySchema` admits all three (the BLOCKS section has the
 * identical structure to ENTITIES per the Autodesk DXF reference / ezdxf
 * `BlockLayout`, and this repo's own `apps/web/public/abi1-test.dxf` fixture
 * carries ELEVEN heavy POLYLINEs inside block definitions), but neither EXPLODE
 * path had a case for them: both `explodeChild` (the persisted document
 * reducer, `apps/web/src/app/state/blockReducerHelpers.ts`) and
 * `explodeChildEntity` (the shell-preview mutation, `./blockMutations.ts`) fell
 * through to a `default:` arm returning `[]`, while
 * `explodeBlockInsertInDocument` removed the parent INSERT regardless. EXPLODE
 * therefore DELETED every such child, silently and with no audit row — the
 * worst class of defect, on exactly the drawing the admission was made for.
 *
 * # One truth for the affine, not new per-type math
 *
 * The per-type affine already exists and is reviewed:
 * {@link import("./geometryTransforms").transformEntityCoords} has a full arm
 * for each of the three (heavy-POLYLINE OCS lift via `ocsPlanarTransform`,
 * mirror bulge handedness, curve-fit tangent directions, the ACAD_TABLE
 * horizontal-direction vector, the OLE frame's FOUR-corner image). EXPLODE
 * hands it the INSERT's own point map, so a child that is exploded and a child
 * that is MOVEd/ROTATEd/MIRRORed at top level can never drift apart.
 *
 * This module adds only what EXPLODE has and a top-level edit does not:
 *
 *   - the WIDTH FACTOR for a heavy polyline's groups 40/41 (the band), on the
 *     SAME similarity rule the render path uses — see
 *     {@link explodedBandWidthScale}. Only the CHOICE of factor lives here;
 *     APPLYING it is `./scaleEntityRadii.ts`'s own `Polyline` arm, the same
 *     walk `geometry/scale-entities` runs;
 *   - the Z leg of the INSERT transform, which the 2D `TransformFn` contract
 *     cannot carry — see {@link ExplodeChildZMap};
 *   - own-identity HANDLE stripping: the source child STAYS in the block
 *     definition, so the exploded copy must not carry its group 5 / 330 / 360
 *     (`stripExplodedChildHandles`);
 *   - the EXPRESSIBILITY predicate — {@link explodeChildRefusal}.
 *
 * # Why two of the three can be REFUSED, and exactly when
 *
 * EXPLODE must write a PERSISTED entity, so the enclosing INSERT affine has to
 * be absorbable by that entity's own fields. For the heavy POLYLINE it always
 * is (it is a point set). For the other two it is not, and the boundary is
 * sharp:
 *
 *   - `ACAD_TABLE` IS a block reference and carries NO scale factors — its only
 *     placement state is the insertion point and the horizontal-direction
 *     vector, and what is DRAWN is the pre-evaluated `*T<N>` graphics block.
 *     The block-child render arm therefore COMPOSES the INSERT affine onto that
 *     reference rather than cloning the entity
 *     (`apps/web/src/app/scene3d/adapters/typed/blockChildTable.ts`: "a table
 *     inside a ×2 INSERT would draw at ×1, half its real size"). A clone can
 *     reproduce the drawn table EXACTLY when, and only when, the INSERT's
 *     linear part is a PROPER ROTATION — `|sx| = |sy| = 1` with `sx·sy > 0`.
 *     This is the same boundary
 *     {@link import("./tableScaleRefusal").isTableScaleRefused} draws for
 *     `geometry/scale-entities` (which likewise admits `factor === -1`, a
 *     180° point reflection, and declines every genuine resize).
 *   - `OLE2FRAME` stores TWO opposite WCS corners and no rotation: the frame is
 *     axis-aligned in the file BY CONSTRUCTION (Autodesk DXF reference; ezdxf's
 *     `OLE2Frame` implements no `transform()` at all). The block-child render
 *     arm draws the true rotated quad and deliberately refuses to write it back
 *     (`.../typed/blockChildOleFrame.ts`, `transformBlockChild.ts`'s `case "OLE
 *     Frame": return null`). A clone is exact when the INSERT's rotation is a
 *     multiple of 90° — any scale and any mirror are fine, because those keep
 *     an axis-aligned rectangle axis-aligned — and cannot express the shape
 *     otherwise.
 *
 * A refusal is TOTAL and VISIBLE, never partial: the caller leaves the whole
 * INSERT alone (the parent is NOT removed) and reports an RU+EN warning-tone
 * audit row, the same shape the SCALE × Table refusal uses. CLAUDE.md
 * ПРИОРИТЕТ #1's one allowed exception is a documented, visible, known-incomplete
 * subsystem; a silently half-exploded INSERT is not one.
 *
 * COMPLETING the two refusals needs the same two things their sibling refusals
 * need: for the table, a `*T<N>` layout engine that rebuilds the graphics block
 * in the SAME `EntityMutation` as a 141/142 write (see `./tableScaleRefusal.ts`);
 * for the OLE frame, a rotation channel on the entity, which would make the
 * model lie about the file and therefore needs the DXF-level answer first.
 */

import type {
  BlockChildEntity,
  DrawingEntity,
  OleFrameEntity,
  PolylineEntity,
  TableEntity,
} from "@dxf-ai-editor/drawing-model";
// Value imports: the group-70 SUBTYPE decoder and the group-210 default test
// are the ONE truth for those bits (`drawing-model` → `dxf-io`'s
// `dxfPolylineSubtype`, copied from ezdxf `Polyline.get_mode`). Re-deriving
// either here would be a second truth for the file format.
import { isDefaultExtrusion, polylineSubtype } from "@dxf-ai-editor/drawing-model";

import { transformEntityCoords } from "./geometryTransforms";
// The SIZE-like companion to `transformEntityCoords`: its own `Polyline` arm
// already multiplies groups 40 / 41 (entity default + per vertex, presence
// preserved) by `|factor|`. EXPLODE reuses it rather than re-deriving the
// walk, so a width that travels through an INSERT and a width that travels
// through `geometry/scale-entities` can never drift apart.
import { scaleEntityRadii } from "./scaleEntityRadii";

/** The three kinds this module owns. */
export type ExplodablePolylineTableOle =
  | PolylineEntity
  | TableEntity
  | OleFrameEntity;

/** The 2D planar point map of the enclosing INSERT — `transformEntityCoords`'s own contract. */
export type ExplodePointMap = (p: { x: number; y: number }) => {
  x: number;
  y: number;
};

/**
 * The Z leg of the INSERT transform, which a 2D {@link ExplodePointMap} cannot
 * carry: `z' = (z − basePointZ)·scaleZ + insertionZ`.
 *
 * Exactly the render path's own `elevationOf`
 * (`scene3d/adapters/typed/transformBlockChild.ts`) and the exploded-ellipse
 * centre-Z rule (`./ellipseExplodeGeometry.ts`'s `worldCenterZ`) — ONE formula,
 * three consumers, so an elevated block child lands at the same Z whether it is
 * drawn, exploded through the ellipse arm, or exploded through this one.
 */
export interface ExplodeChildZMap {
  readonly scaleZ: number;
  readonly insertionZ: number;
  readonly basePointZ: number;
}

/** `z' = (z − basePointZ)·scaleZ + insertionZ`. */
function mapZ(z: number, zMap: ExplodeChildZMap): number {
  return (z - zMap.basePointZ) * zMap.scaleZ + zMap.insertionZ;
}

/** `true` when {@link mapZ} is the identity, i.e. nothing on the Z leg to apply. */
function zMapIsIdentity(zMap: ExplodeChildZMap): boolean {
  return zMap.scaleZ === 1 && zMap.insertionZ - zMap.basePointZ === 0;
}

/** The INSERT placement the expressibility predicates read. */
export interface ExplodeInsertPlacement {
  readonly rotationDeg: number;
  readonly scaleX: number;
  readonly scaleY: number;
}

/** Why EXPLODE declines an INSERT rather than half-carrying one of its children. */
export type ExplodeChildRefusal = "table-not-rigid" | "ole-frame-oblique";

/**
 * Degrees of slack on the "the rotation is a multiple of 90°" test. A drawing
 * expresses no angle below this, and `rotation` arrives as an authored scalar
 * (never a float-probed one), so this only absorbs the representation error of
 * a value such as `270` that travelled through a parse. Same order as
 * `geometryTransforms.ts`'s own `DIRECTION_NOOP_TOLERANCE`.
 */
const RIGHT_ANGLE_TOLERANCE_DEG = 1e-9;

/**
 * `true` when `rotationDeg` is a multiple of 90° within
 * {@link RIGHT_ANGLE_TOLERANCE_DEG}, approached from either side.
 *
 * `Math.abs(rotationDeg % 90)` rather than the usual `((r % 90) + 90) % 90`
 * positive-modulo dance: JS `%` keeps the sign of the dividend, so the absolute
 * value is already the distance to the multiple BELOW for a positive angle and
 * — by the symmetry of the test around each multiple — the right residual for a
 * negative one too. It is also EXACT for a small angle, where the `+ 90` of the
 * other form is not: `1e-9 + 90` rounds, so `((1e-9 % 90) + 90) % 90` comes back
 * as `1.0000036e-9` and the tolerance boundary could not be stated, let alone
 * tested.
 *
 * The upper half is written as `residual >= 90 - tolerance` rather than
 * `90 - residual <= tolerance` for the same reason: `90 - tolerance` is exact,
 * so the inclusive bound is a value a test can name, while `90 - residual`
 * subtracts from a number that has already lost bits and lands near it rather
 * than on it.
 */
function isAxisPreservingRotation(rotationDeg: number): boolean {
  // targeted-cad-mutation-testing EQUIVALENT MUTANT (round 13, verified
  // survivor): `ConditionalExpression` forcing this guard to `false`, i.e.
  // letting a non-finite angle fall through. Equivalent by IEEE-754: `NaN % 90`
  // and `(+/-Infinity) % 90` are both `NaN`, `Math.abs(NaN)` is `NaN`, and
  // EVERY comparison against `NaN` is `false` — so both disjuncts below are
  // false and the function returns `false` either way. The guard is kept
  // because it states the intent at the top instead of leaving a reader to
  // re-derive the NaN algebra, not because the two paths differ.
  if (!Number.isFinite(rotationDeg)) return false;
  const residual = Math.abs(rotationDeg % 90);
  // `90 - tolerance` inline rather than as a module-level `const`: a constant
  // at module scope is a STATIC initializer, and this repo's Stryker setup
  // cannot attribute a test to one, so a wrong bound in it would survive as an
  // unattributable false negative instead of being killed by the boundary
  // tests that do cover it. The subtraction is exact and free.
  return (
    residual <= RIGHT_ANGLE_TOLERANCE_DEG ||
    residual >= 90 - RIGHT_ANGLE_TOLERANCE_DEG
  );
}

/**
 * Why EXPLODE cannot carry `child` under `placement`, or `null` when it can.
 *
 * Total and pure: every kind other than `Table` / `OLE Frame` — the heavy
 * POLYLINE included — is always carryable, and so are those two under a
 * placement their own fields can absorb (module header for the derivation).
 */
export function explodeChildRefusal(
  child: BlockChildEntity,
  placement: ExplodeInsertPlacement,
): ExplodeChildRefusal | null {
  const { rotationDeg, scaleX, scaleY } = placement;
  if (child.type === "Table") {
    // The third conjunct is the ORIENTATION test: the first two admit every
    // unit-magnitude placement, including the two mirrors, and only `sx*sy > 0`
    // separates a proper rotation from a reflection.
    //
    // targeted-cad-mutation-testing EQUIVALENT MUTANTS (round 13, verified
    // survivors), both equivalent because of the SAME upstream invariant — the
    // two conjuncts to their left force `|sx| = |sy| = 1` before the third is
    // ever evaluated, so `scaleX` and `scaleY` are each exactly `+/-1`:
    //   - `EqualityOperator` `> 0` -> `>= 0`: the product is exactly `+/-1` and
    //     can never be `0`, the only value the two operators disagree on.
    //   - `ArithmeticOperator` `scaleX * scaleY` -> `scaleX / scaleY`: for
    //     operands of equal magnitude the quotient and the product have the
    //     same sign (`+/-1` either way), so the `> 0` test reads identically.
    const rigid =
      Math.abs(scaleX) === 1 && Math.abs(scaleY) === 1 && scaleX * scaleY > 0;
    return rigid ? null : "table-not-rigid";
  }
  if (child.type === "OLE Frame") {
    return isAxisPreservingRotation(rotationDeg) ? null : "ole-frame-oblique";
  }
  return null;
}

/** What an EXPLODE dispatch will actually do to ONE block definition's children. */
export interface ExplodeInsertRefusalSummary {
  /** How many `ACAD_TABLE` children the placement cannot carry. */
  readonly refusedTableCount: number;
  /** How many `OLE2FRAME` children the placement cannot carry. */
  readonly refusedOleFrameCount: number;
  /** `true` when the INSERT must be left ENTIRELY alone (nothing exploded, parent kept). */
  readonly refused: boolean;
}

/**
 * Split a block definition's children into "EXPLODE can carry every one of
 * them" and "it cannot", using {@link explodeChildRefusal} as the single
 * predicate — the ONE validity truth the persisted document reducer (which
 * keeps the INSERT) and the shell reducer (which reports the refusal) both
 * read, so the shell can never report a refusal the document did not make nor
 * stay silent about one it did.
 *
 * A MINSERT needs no per-cell evaluation: the per-cell matrices differ by a
 * TRANSLATION only, and neither predicate reads the translation.
 */
export function summarizeExplodeInsertRefusal(
  children: readonly BlockChildEntity[],
  placement: ExplodeInsertPlacement,
): ExplodeInsertRefusalSummary {
  let refusedTableCount = 0;
  let refusedOleFrameCount = 0;
  for (const child of children) {
    switch (explodeChildRefusal(child, placement)) {
      case "table-not-rigid":
        refusedTableCount += 1;
        break;
      case "ole-frame-oblique":
        refusedOleFrameCount += 1;
        break;
      // No `default:` arm — `explodeChildRefusal` returns `null` for a
      // carryable child and falling out of the switch is exactly the "count
      // nothing" the arm would have written. An explicit `default: break;`
      // would be a statement no input can distinguish from its absence
      // (targeted-cad-mutation-testing: every mutant of it is equivalent by
      // construction), so the absence is the honest shape.
    }
  }
  // Built inline rather than returned from a shared module-level constant:
  // a `const` at module scope is a STATIC mutant (evaluated once at load), and
  // this repo's Stryker setup cannot attribute a test to one — a wrong literal
  // in it survives as an unattributable false negative rather than being killed
  // by the shape assertion that does cover it. There is nothing to share here.
  return {
    refusedTableCount,
    refusedOleFrameCount,
    refused: refusedTableCount > 0 || refusedOleFrameCount > 0,
  };
}

/**
 * The factor a heavy polyline's WIDTH fields (groups 40 / 41, entity-level and
 * per-vertex) take through an INSERT.
 *
 * `|sx|` under a SIMILARITY (`|sx| === |sy|` — the identity, every uniform
 * scale, every mirror, every rotation), because a similarity multiplies every
 * length by the same number. `1` otherwise: the image of a constant-width band
 * under, say, `S(2, 3)` is not a band of any single width, so the render path
 * keeps the AUTHORED width and maps the finished band through a frame instead
 * (`scene3d/adapters/typed/blockChildBandFrame.ts` — `isSimilarityTransform` is
 * the same predicate, deliberately). A persisted entity has no frame to carry,
 * so the authored width is what it keeps, and this is the ONE place the choice
 * is made rather than a scalar guessed at each call site.
 *
 * KNOWN RESIDUAL (documented, not silent): a WIDE polyline exploded out of a
 * NON-similarity INSERT therefore draws its band at the authored width rather
 * than at the mapped band's varying width. It is the same residual the
 * LWPOLYLINE / Revision Cloud explode arms carry for their own width scalars,
 * and closing it needs a width representation a DXF polyline does not have.
 * A zero-width polyline — every heavy polyline in `abi1-test.dxf` — and every
 * similarity INSERT are exact.
 */
export function explodedBandWidthScale(scaleX: number, scaleY: number): number {
  return Math.abs(scaleX) === Math.abs(scaleY) ? Math.abs(scaleX) : 1;
}

/**
 * Drop the own-identity DXF handles of an exploded copy.
 *
 * EXPLODE does NOT delete the block definition, so the source child keeps
 * living inside it: carrying its group 5 / 330 / 360 onto the new top-level
 * entity would put TWO records at one handle and TWO owners on one extension
 * dictionary — invalid DXF, and the same trap
 * {@link import("./nestedInsertExplode").stripExplodedInsertRepresentationHandles}
 * documents for a nested INSERT's representation linkage. Every other
 * `explodeChild` arm gets this for free by building a fresh literal; these three
 * spread the source entity (they must — `contentGroups`, `binaryChunks`,
 * `seqendMetadata`, per-vertex XDATA and the unmapped-group passthrough are
 * user content that a field allow-list would delete), so the handles are
 * removed explicitly.
 *
 * REFERENCE handles are deliberately KEPT: a table's `tableStyleHandle` (342)
 * and `blockRecordHandle` (343) point at shared objects the copy legitimately
 * references, exactly as the nested-INSERT arm keeps `blockName`.
 */
function stripExplodedChildHandles<T extends ExplodablePolylineTableOle>(
  entity: T,
): T {
  const next = { ...entity };
  delete next.ownerHandle;
  delete next.extensionDictionaryHandle;
  if (next.type === "Polyline") {
    const polyline = next as PolylineEntity;
    delete polyline.seqendHandle;
    if (polyline.seqendMetadata) {
      polyline.seqendMetadata = stripSeqendMetadataIdentity(
        polyline.seqendMetadata,
      );
    }
    polyline.vertices = polyline.vertices.map((vertex) => {
      const copy = { ...vertex, handle: "" };
      delete copy.extensionDictionaryHandle;
      return copy;
    });
  }
  return next;
}

/**
 * The terminating SEQEND's record, minus the ONE identity field its type does
 * not already exclude.
 *
 * @codex PR #785 round 14, FINDING 1. This used to be `delete
 * polyline.seqendMetadata`, which threw the WHOLE record away — and that record
 * is passthrough user content, not identity: `PolylineSeqendMetadata`
 * (`drawing-model/entityPolylineTableOle.ts`) is declared as
 * `Omit<DxfSeqendEntity, "kind" | "type" | "geometry" | "common"> & { common:
 * Omit<DxfCommonEntityProps, "handle" | "ownerHandle" | "space" | "layoutName" |
 * "forceLayerEmit"> }`, i.e. the SEQEND's own group 5 and its group 330 are
 * ALREADY excluded AT THE TYPE LEVEL (the handle lives in
 * `PolylineEntity.seqendHandle`, which this arm still deletes, and the owner is
 * re-derived as the parent POLYLINE's handle by the writer — ezdxf 1.4.4
 * `Polyline.new_seqend`). What the blanket delete destroyed was the terminator's
 * layer / linetype / colour / lineweight / thickness / extrusion, its
 * `102 {ACAD_REACTORS` and unmapped groups (`extraGroups`), its non-canonical
 * `100` subclass extension and its `1001` XDATA — the very channels that type is
 * an `Omit` (rather than an allow-list of four names) to guarantee survive.
 * The exploded copy is a NEW top-level POLYLINE that writes its OWN terminator,
 * so it must keep that terminator's record exactly as the clone path does.
 *
 * `common.extensionDictionaryHandle` is the one survivor that cannot be shared:
 * group 360 names an OBJECTS-section DICTIONARY whose OWNER is this SEQEND, and
 * the source SEQEND stays alive inside the block definition — two owners on one
 * extension dictionary is the same invalid DXF the entity-level and per-VERTEX
 * `extensionDictionaryHandle` deletes above prevent. `materialHandle` (347) and
 * `plotStyleHandle` (390) stay: like the table's `tableStyleHandle`, they point
 * at SHARED objects a copy legitimately references.
 *
 * Returns a fresh record (never mutates the source): the child is still in the
 * block definition and must come out of EXPLODE untouched.
 */
function stripSeqendMetadataIdentity(
  metadata: NonNullable<PolylineEntity["seqendMetadata"]>,
): NonNullable<PolylineEntity["seqendMetadata"]> {
  const common = { ...metadata.common };
  delete common.extensionDictionaryHandle;
  return { ...metadata, common };
}

/**
 * Apply the Z leg of the INSERT to a transformed child.
 *
 * A 2D `TransformFn` leaves every Z as authored, which is right for the XY
 * affine and wrong for an INSERT with a `scaleZ`, an elevated insertion point
 * or a non-zero block base-point Z. Applied here, per kind, exactly where the
 * render path applies it:
 *
 *   - heavy POLYLINE — the 2D plane travels on the `elevation` dummy point's Z;
 *     a 3D polyline / mesh VERTEX carries real WCS Z and rides it too, while a
 *     2D one's per-vertex Z is not geometric and stays as authored (the same
 *     split `transformBlockChild`'s `planar` branch and
 *     `wcsBoundsLeaf.heavyPolylineLocal` make). A poly-face FACE record's
 *     10/20/30 is a FILLER, never a point, so it is left alone — and
 *     `transformEntityCoords` leaves it alone on XY for the same reason.
 *   - `ACAD_TABLE` — the insertion point's Z, read from the SOURCE child (see
 *     below).
 *   - `OLE2FRAME` — both stored corners' Z, which the arm upstream carries per
 *     corner through `transformPoint3D`.
 *
 * ## Why the TABLE reads its Z from the source and runs unconditionally
 *
 * The Z has to be mapped through the INSERT's Z LEG — elevation and z-scale —
 * and `transformEntityCoords` does not do that for any kind: its contract is
 * the PLANAR map, and the Z leg belongs to the explode placement. So the
 * authoritative pre-transform value is read from the SOURCE child and
 * re-mapped here.
 *
 * HISTORY, because the original reason is no longer the reason. This arm was
 * first written to work around `transformEntityCoords`'s `Table` arm mapping
 * the insertion point with the 2D `transformPoint`, which returns `{ x, y }`
 * and DROPPED group 30 outright — reading the Z back off the transformed
 * entity yielded `undefined`, and `mapZ(undefined)` yielded `NaN`, which is
 * how it was caught. That upstream defect is fixed (`234ed39`: the arm now
 * uses `transformPoint3D`, like its `OLE Frame` sibling always did), so the
 * source read is no longer load-bearing for mere PRESERVATION — `transformed`
 * and `source` now carry the same Z, and the spread below is overwritten by
 * the `mapZ` result either way. It is kept because reading the placement's
 * input from the source is the clearer statement of intent, and because the
 * `mapZ` itself is still required.
 *
 * The arm stays unconditional for the same reason it is harmless to be: with
 * an identity Z leg `mapZ(source.z) === source.z`, so it is a no-op rather
 * than a correction. It is NOT relied upon to repair a dropped field any more.
 *
 * For the other two kinds the upstream arm preserves Z verbatim, so source and
 * transformed agree and the identity short-circuit below is a true no-op.
 *
 * KNOWN RESIDUAL (documented): a 2D heavy polyline with a NON-DEFAULT group-210
 * extrusion has its plane re-derived by `ocsPlanarTransform` under a
 * Z-PRESERVING lift (`transformEntityCoords`'s contract), so a non-identity Z
 * leg and a tilted child do not compose exactly. The guard below skips the
 * polyline/OLE pass when the Z leg IS the identity — `scaleZ === 1` with a zero
 * insertion/base-point offset, which is every INSERT in the fixture and the
 * overwhelming majority in practice — so the tilted case is exact there, and
 * elsewhere the plane is off by the Z leg alone. Closing it needs
 * `transformEntityCoords` to take a 3D point map, a change to a shared
 * top-level-edit contract that belongs in its own PR.
 */
function applyExplodeZ<T extends ExplodablePolylineTableOle>(
  transformed: T,
  source: T,
  zMap: ExplodeChildZMap,
): T {
  switch (transformed.type) {
    case "Table":
      return {
        ...transformed,
        insertionPoint: {
          ...transformed.insertionPoint,
          z: mapZ(
            (source as TableEntity).insertionPoint.z ?? 0,
            zMap,
          ),
        },
      };
    case "Polyline": {
      if (zMapIsIdentity(zMap)) return transformed;
      const planar = polylineSubtype(transformed) === "2d";
      return {
        ...transformed,
        elevation: {
          ...transformed.elevation,
          z: mapZ(transformed.elevation.z ?? 0, zMap),
        },
        vertices: planar
          ? transformed.vertices
          : transformed.vertices.map((vertex) =>
              vertex.faceVertexIndices !== undefined
                ? vertex
                : {
                    ...vertex,
                    location: {
                      ...vertex.location,
                      z: mapZ(vertex.location.z ?? 0, zMap),
                    },
                  },
            ),
      };
    }
    case "OLE Frame": {
      if (zMapIsIdentity(zMap)) return transformed;
      return {
        ...transformed,
        upperLeft: {
          ...transformed.upperLeft,
          z: mapZ(transformed.upperLeft.z ?? 0, zMap),
        },
        lowerRight: {
          ...transformed.lowerRight,
          z: mapZ(transformed.lowerRight.z ?? 0, zMap),
        },
      };
    }
    default: {
      // UNREACHABLE by construction: `T extends ExplodablePolylineTableOle` has
      // exactly the three members the cases above name, and the `never`
      // assignment is what makes a fourth one a compile error rather than a
      // silent fall-through. targeted-cad-mutation-testing (round 13): every
      // mutant Stryker plants in this arm — removing the `default:` label,
      // emptying the block, mutating the literals inside it — is EQUIVALENT,
      // because no input can reach it. Deleting the arm to make the mutants go
      // away would delete the exhaustiveness check itself, which is the whole
      // point of the arm.
      const exhaustive: never = transformed;
      return exhaustive;
    }
  }
}

/**
 * Carry ONE heavy `POLYLINE` / `ACAD_TABLE` / `OLE2FRAME` block child out
 * through the enclosing INSERT.
 *
 * The caller must have established that the placement is carryable
 * ({@link explodeChildRefusal} / {@link summarizeExplodeInsertRefusal}) — this
 * function transforms, it does not decide. `id` / `spaceId` / the resolved
 * layer-colour-linetype-lineweight base belong to the caller's arm, exactly as
 * for every other kind.
 */
export function explodeBlockChildPolylineTableOle<
  T extends ExplodablePolylineTableOle,
>(child: T, fn: ExplodePointMap, placement: ExplodeInsertPlacement, zMap: ExplodeChildZMap): T {
  // `transformEntityCoords` owns the per-type affine (module header): the
  // heavy-POLYLINE OCS lift + bulge handedness + curve-fit tangents, the table's
  // horizontal-direction vector, the OLE frame's four-corner image.
  const moved = transformEntityCoords(child as unknown as DrawingEntity, fn) as T;
  const withZ = applyExplodeZ(moved, child, zMap);
  // Widths are SIZE-like, so they belong to `transformEntityCoords`'s SIZE
  // companion, not to the point map — the same split every SCALE dispatch
  // makes. `scaleEntityRadii` is a total function: a `Table` / `OLE Frame`
  // has no size-like field and comes back untouched, so no kind test is
  // needed here and the factor (1 for every non-similarity INSERT) decides.
  const withWidths = scaleEntityRadii(
    withZ as unknown as DrawingEntity,
    { x: 0, y: 0 },
    explodedBandWidthScale(placement.scaleX, placement.scaleY),
  ) as unknown as T;
  return stripExplodedChildHandles(withWidths);
}

/**
 * `true` when `child` is one of the three kinds this module carries — the
 * narrowing both EXPLODE arms use so their `case` labels and this module's
 * contract cannot drift.
 */
export function isPolylineTableOleChild(
  child: BlockChildEntity,
): child is BlockChildEntity & ExplodablePolylineTableOle {
  return (
    child.type === "Polyline" ||
    child.type === "Table" ||
    child.type === "OLE Frame"
  );
}

/**
 * A non-default extrusion on a 2D heavy polyline — the ONE case where the Z
 * residual documented on {@link applyExplodeZ} can bite. Exported so the tests
 * that pin the residual name it from the production predicate rather than
 * re-deriving the bit test.
 */
export function explodeZResidualApplies(
  child: BlockChildEntity,
  zMap: ExplodeChildZMap,
): boolean {
  if (child.type !== "Polyline") return false;
  if (zMapIsIdentity(zMap)) return false;
  return polylineSubtype(child) === "2d" && !isDefaultExtrusion(child.extrusion);
}
